import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { acceptHMRUpdate, defineStore } from 'pinia';
import dayjs from '@/dayjs';
import { useClock } from '@/helpers/clock';
import { flash, modal, notification } from '@/helpers/ui';
import i18n from '@/i18n';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesStore } from '@/stores/services';
import type { ServicePriceChange } from '@/types';

const GET_SERVICE_PRICE_CHANGE_SCHEDULES = gql`
  query getServicePriceChangeSchedules {
    servicePriceChangeSchedules {
      id
      scheduledAt
    }
  }
`;

const GET_SERVICE_PRICE_CHANGE_SCHEDULES_WITH_CHANGES = gql`
  query getServicePriceChangeSchedules {
    servicePriceChangeSchedules {
      id
      scheduledAt
      servicePriceChanges {
        id
        newPrice
        originalPrice
        service {
          id
        }
      }
    }
  }
`;

const CANCEL_SERVICE_PRICE_CHANGE_SCHEDULE = gql`
  mutation cancelServicePriceChangeSchedule($input: CancelServicePriceChangeScheduleInput!) {
    cancelServicePriceChangeSchedule(input: $input) {
      servicePriceChangeSchedule {
        id
      }
    }
  }
`;

type ServicePriceChangeSchedule = {
  id: number;
  scheduledAt: string;
  servicePriceChanges: ServicePriceChange[];
};

export const useServicesPriceScheduleStore = defineStore(
  'servicesPriceSchedule',
  () => {
    const scheduledPriceUpdate = ref<ServicePriceChangeSchedule | null>(null);
    const serviceAmount = computed(
      () => scheduledPriceUpdate.value?.servicePriceChanges?.length || 0,
    );
    const scheduleFetched = ref(false);

    const now = useClock().now;

    const getScheduledPriceUpdate = () => {
      const { onResult } = useQuery(GET_SERVICE_PRICE_CHANGE_SCHEDULES);
      onResult(({ data }) => {
        scheduledPriceUpdate.value =
          data.servicePriceChangeSchedules?.[0] || null;
        scheduleFetched.value = true;
      });
    };

    const getScheduledServicePriceChanges = () =>
      new Promise<void>((resolve) => {
        const { onResult } = useQuery(
          GET_SERVICE_PRICE_CHANGE_SCHEDULES_WITH_CHANGES,
        );
        onResult(({ data }) => {
          scheduledPriceUpdate.value =
            data.servicePriceChangeSchedules?.[0] || null;
          resolve();
        });
      });

    const scheduleShouldBeApplied = computed(() => {
      // We're adding 30 seconds to the scheduled date to give the backend time to actually update the prices
      // This is obviously not a guarantee, but it's the best we can do without websockets
      return (
        scheduledPriceUpdate.value &&
        dayjs(scheduledPriceUpdate.value.scheduledAt)
          .add(30, 'seconds')
          .isSameOrBefore(now.value)
      );
    });

    watch(scheduleShouldBeApplied, (newVal) => {
      if (newVal) {
        const { getServices } = useServicesStore();
        const { getServiceGroups } = useServiceGroupsStore();

        Promise.all([getServices(), getServiceGroups()]).then(() => {
          notification({ name: 'service-prices-updated', persist: true });
          scheduledPriceUpdate.value = null;
        });
      }
    });

    const cancelScheduledPriceUpdate = () => {
      const { mutate } = useMutation(CANCEL_SERVICE_PRICE_CHANGE_SCHEDULE);

      return new Promise<void>((resolve) => {
        modal('confirmation', {
          message: i18n.t('services.price_update.cancel_schedule_confirmation'),
        }).then(() => {
          mutate({
            input: {
              servicePriceChangeScheduleId: scheduledPriceUpdate.value?.id,
            },
          }).then(() => {
            flash(i18n.t('global.flash.service_price_update_cancelled'));
            scheduledPriceUpdate.value = null;
            resolve();
          });
        });
      });
    };

    return {
      scheduledPriceUpdate,
      serviceAmount,
      scheduleFetched,
      getScheduledPriceUpdate,
      getScheduledServicePriceChanges,
      cancelScheduledPriceUpdate,
    };
  },
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useServicesPriceScheduleStore, import.meta.hot),
  );
}
